// Shared dashboard constants and date/format helpers.
import { CURRENCY_LOCALE } from '../../lib/format';

/** Locale for dates and numbers across the dashboard (same as the currency locale). */
export const DATE_LOCALE = CURRENCY_LOCALE;
export const BUSINESS_TIME_ZONE = 'America/Guatemala';

function dateOnlyAtNoonUtc(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

/** Formats a SQL date-only business date without depending on the browser timezone. */
export function formatBusinessDate(value: string, locale: string = DATE_LOCALE): string {
  const date = dateOnlyAtNoonUtc(value);
  return date
    ? new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: BUSINESS_TIME_ZONE,
      }).format(date)
    : value;
}

/** Formats the business month (optionally offset) from a SQL date-only value. */
export function formatBusinessMonth(
  value: string,
  monthOffset = 0,
  locale: string = DATE_LOCALE,
): string {
  const date = dateOnlyAtNoonUtc(value);
  if (!date) return '';
  const month = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + monthOffset, 1, 12));
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    timeZone: BUSINESS_TIME_ZONE,
  }).format(month);
}

/** Vertical gap between dashboard sections, in px. */
export const SECTION_GAP = 24;

/** Long human date (weekday, day, month, year) in the given locale. */
export function formatDateLong(date: Date, locale: string = DATE_LOCALE): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

/** Capitalizes the first letter of a string. */
export function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
